import Decimal from "decimal.js"
import { useMemo } from "react"

import { calculateDiscountsByOrder } from "./helpers"

export const useCalculateDiscountAmounts = ({
  productSaleOriginalAmount,
  productSaleTaxRate,
  productSaleTaxes,
  currentDiscounts,
}) => {
  const discountsWithAmount = useMemo(() => {
    return currentDiscounts.map(
      calculateDiscountsByOrder(productSaleOriginalAmount)
    )
  }, [currentDiscounts, productSaleOriginalAmount])

  const newDiscountTotal = useMemo(() => {
    return discountsWithAmount.reduce(
      (sum, { amount }) => new Decimal(sum).add(amount).toNumber(),
      0
    )
  }, [discountsWithAmount])

  const newSubtotal = useMemo(() => {
    return new Decimal(productSaleOriginalAmount)
      .minus(newDiscountTotal)
      .toNumber()
  }, [productSaleOriginalAmount, newDiscountTotal])

  const newProductSaleTaxes = useMemo(() => {
    if (!productSaleTaxes || productSaleTaxes.length === 0) {
      return []
    }

    const taxableAmount = new Decimal(newSubtotal)

    return productSaleTaxes.map((tax) => ({
      rate: tax.rate,
      tax_amount: taxableAmount.mul(tax.rate).round().toNumber(),
      tax_type: tax.tax_type,
      name: tax.name,
      source_type: tax.source_type,
      source_id: tax.source_id,
    }))
  }, [productSaleTaxes, newSubtotal])

  const newTaxAmount = useMemo(() => {
    if (newProductSaleTaxes.length > 0) {
      return newProductSaleTaxes.reduce(
        (sum, tax) => new Decimal(sum).add(tax.tax_amount).toNumber(),
        0
      )
    }

    if (!productSaleTaxRate) {
      return 0
    }

    return Number(new Decimal(newSubtotal).mul(productSaleTaxRate).toFixed(0))
  }, [newProductSaleTaxes, newSubtotal, productSaleTaxRate])

  const newTotal = useMemo(() => {
    return Number(new Decimal(newSubtotal).add(newTaxAmount).toFixed(0))
  }, [newSubtotal, newTaxAmount])

  return {
    discountsWithAmount,
    newDiscountTotal,
    newSubtotal,
    newTaxAmount,
    newTotal,
    newProductSaleTaxes,
  }
}
